/**
 * @fileoverview GameChannelContext for web
 * Provides WebSocket-based game channel state management via useGameLivestream hook
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  ReactNode,
} from 'react'
import useGameLivestream from '../../hooks/useGameLivestream'
import type {
  WsTimelineState,
  WsGameState,
} from '@sportninja/common/utils/wsTimelineTransformer'

interface StreamState {
  type: string
  livestreamId: string | null
  broadcastState: string
  streamUrl: string | null
  broadcastId: string | null
  isLive: boolean
  wasLive: boolean
  hasRecording: boolean
  recordingUrl: string | null
}

interface GameInfo {
  homeTeamName: string | null
  visitingTeamName: string | null
  scheduleName: string | null
}

interface GameChannelContextValue {
  // Multi-stream state
  streams: Record<string, StreamState>
  activeStreams: StreamState[]
  activeStreamCount: number
  availableStreams: StreamState[]
  availableStreamCount: number
  selectedStreamType: string | null
  selectedStream: StreamState | null
  effectiveStream: StreamState | null

  // Legacy compatibility
  isLive: boolean
  streamUrl: string | null
  broadcastId: string | null
  broadcastState: string

  // Connection state
  connectionState: string
  error: string | null
  activeGameId: string | null

  // Game info
  gameInfo: GameInfo

  // Scores
  wsHomeScore: number | null
  wsVisitingScore: number | null
  lastScoreUpdate: number | null

  // WebSocket timeline events
  wsTimelineEvents: WsTimelineState
  wsGameState: WsGameState
  resetWsState: () => void
  resetWsTimelineOnly: () => void

  // Actions
  setActiveGameId: (gameId: string | null) => void
  setInitialLivestreams: (livestreams: any[] | null) => void
  selectStream: (streamType: string) => void
  setSelectedStreamType: (streamType: string | null) => void
  clearStreamSelection: () => void
}

const GameChannelContext = createContext<GameChannelContextValue | null>(null)

interface GameChannelProviderProps {
  children: ReactNode
}

export const GameChannelProvider: React.FC<GameChannelProviderProps> = ({
  children,
}) => {
  const [activeGameId, setActiveGameId] = useState<string | null>(null)
  const [initialLivestreams, setInitialLivestreams] = useState<any[] | null>(
    null
  )

  const {
    // Multi-stream state
    streams,
    activeStreams,
    activeStreamCount,
    availableStreams,
    availableStreamCount,
    selectedStreamType,
    setSelectedStreamType,
    selectedStream,

    // Legacy compatibility
    isLive,
    streamUrl,
    broadcastId,
    broadcastState,

    // Connection
    connectionState,
    error,

    // Game info
    gameInfo,

    // Scores
    wsHomeScore,
    wsVisitingScore,
    lastScoreUpdate,

    // WebSocket timeline events
    wsTimelineEvents,
    wsGameState,
    resetWsState,
    resetWsTimelineOnly,
  } = useGameLivestream(activeGameId, initialLivestreams)

  // Effective stream: selected or first available
  const effectiveStream = useMemo(
    () => selectedStream || availableStreams[0] || null,
    [selectedStream, availableStreams]
  )

  // Clear selection if selected stream is no longer available
  useEffect(() => {
    if (
      selectedStreamType &&
      streams[selectedStreamType] &&
      !streams[selectedStreamType].isLive &&
      !streams[selectedStreamType].hasRecording
    ) {
      setSelectedStreamType(null)
    }
  }, [selectedStreamType, streams, setSelectedStreamType])

  // Reset selection when game changes
  useEffect(() => {
    if (!activeGameId) {
      setSelectedStreamType(null)
      setInitialLivestreams(null)
    }
  }, [activeGameId, setSelectedStreamType])

  // Select a specific stream by type
  const selectStream = useCallback(
    (streamType: string) => {
      const stream = streams[streamType]
      if (streamType && stream && (stream.isLive || stream.hasRecording)) {
        setSelectedStreamType(streamType)
      }
    },
    [streams, setSelectedStreamType]
  )

  // Clear stream selection
  const clearStreamSelection = useCallback(() => {
    setSelectedStreamType(null)
  }, [setSelectedStreamType])

  const value = useMemo<GameChannelContextValue>(
    () => ({
      // Multi-stream state
      streams,
      activeStreams,
      activeStreamCount,
      availableStreams,
      availableStreamCount,
      selectedStreamType,
      selectedStream,
      effectiveStream,

      // Legacy compatibility
      isLive,
      streamUrl:
        effectiveStream?.streamUrl ||
        effectiveStream?.recordingUrl ||
        streamUrl,
      broadcastId: effectiveStream?.broadcastId || broadcastId,
      broadcastState: effectiveStream?.broadcastState || broadcastState,

      // Connection state
      connectionState,
      error,
      activeGameId,

      // Game info
      gameInfo,

      // Scores
      wsHomeScore,
      wsVisitingScore,
      lastScoreUpdate,

      // WebSocket timeline events
      wsTimelineEvents,
      wsGameState,
      resetWsState,
      resetWsTimelineOnly,

      // Actions
      setActiveGameId,
      setInitialLivestreams,
      selectStream,
      setSelectedStreamType,
      clearStreamSelection,
    }),
    [
      streams,
      activeStreams,
      activeStreamCount,
      availableStreams,
      availableStreamCount,
      selectedStreamType,
      selectedStream,
      effectiveStream,
      isLive,
      streamUrl,
      broadcastId,
      broadcastState,
      connectionState,
      error,
      activeGameId,
      gameInfo,
      wsHomeScore,
      wsVisitingScore,
      lastScoreUpdate,
      wsTimelineEvents,
      wsGameState,
      resetWsState,
      resetWsTimelineOnly,
      selectStream,
      setSelectedStreamType,
      clearStreamSelection,
    ]
  )

  return (
    <GameChannelContext.Provider value={value}>
      {children}
    </GameChannelContext.Provider>
  )
}

export const useGameChannelContext = (): GameChannelContextValue => {
  const context = useContext(GameChannelContext)
  if (!context) {
    throw new Error(
      'useGameChannelContext must be used within a GameChannelProvider'
    )
  }
  return context
}

export default GameChannelContext
